import { useMemo, useCallback } from "react";
import { useTenant } from "./useTenant";

/**
 * usePermissions
 * Hook estrito e sem "jeitinhos" para controle de acesso via Custom Claims.
 * 
 * Hierarquia de Roles:
 *   super_admin → acesso a TODOS os tenants (plataforma)
 *   owner       → dono da unidade, acesso total ao seu tenant
 *   admin       → administrador root do tenant, acesso total (sem ser dono)
 *   employee    → colaborador com permissões definidas por cargo customizado
 * 
 * Regra:
 * A autorização NÃO consulta o banco de dados.
 * Ou a permissão está injetada no Token de Autenticação, ou o acesso é negado.
 */
export const usePermissions = () => {
    const { claims, loading } = useTenant();

    const role = claims?.role || null;

    const permissions = useMemo(() => {
        return claims?.permissions || {};
    }, [claims]);

    /** Roles com acesso total (não precisam de mapa de permissões) */
    const isFullAccess = role === "super_admin" || role === "owner" || role === "admin";

    /**
     * Verifica permissão individual
     */
    const can = useCallback((permissionPath) => {
        if (!role) return false;
        if (isFullAccess) return true;

        return permissions[permissionPath] === true;
    }, [role, isFullAccess, permissions]);

    /**
     * Verifica array de permissões (se ele tem QUALQUER UMA delas)
     */
    const canAny = useCallback((permsArray = []) => {
        if (!role) return false;
        if (isFullAccess) return true;

        return permsArray.some(p => permissions[p] === true);
    }, [role, isFullAccess, permissions]);

    /**
     * Validação direta de Cargo absoluto 
     */
    const is = useCallback((targetRole) => {
        return role === targetRole;
    }, [role]);

    return {
        can,
        canAny,
        is,
        isFullAccess,
        isSuperAdmin: role === "super_admin",
        isOwner: role === "owner",
        isAdmin: role === "admin",
        isEmployee: role === "employee",
        permissions,
        role,
        loading
    };
};
